import type { UseQueryResult } from '@tanstack/react-query';
import {
  type Control,
  Controller,
  type FieldPath,
  type FieldValues,
} from 'react-hook-form';
import type { SelectParams } from '@/api/select/select.service';
import { Field, FieldError, FieldLabel } from '@/components/ui/field';
import { cn } from '@/lib/utils';
import { AsyncSelect } from '../async-select';
import { type ColSpan, lgSpan, mdSpan, smSpan } from './form-input';

type FormAsyncSelectProps<T extends FieldValues, TOption> = {
  name: FieldPath<T>;
  control: Control<T>;
  label: string;

  placeholder?: string;

  sm?: ColSpan;
  md?: ColSpan;
  lg?: ColSpan;

  className?: string;

  useQuery: (params?: SelectParams) => UseQueryResult<TOption[], Error>;
  getOptionValue: (option: TOption) => string;
  getDisplayValue: (option: TOption) => string;

  /** Guarda o objeto opção inteiro no campo em vez de só o id. */
  returnOption?: boolean;
  onSelect?: (option: TOption | null) => void;

  clearable?: boolean;
  disabled?: boolean;
};

export function FormAsyncSelect<T extends FieldValues, TOption>({
  name,
  control,
  label,
  placeholder,

  sm = 12,
  md = 4,
  lg = 4,

  className,

  useQuery,
  getOptionValue,
  getDisplayValue,

  returnOption,
  onSelect,

  clearable,
  disabled,
}: FormAsyncSelectProps<T, TOption>) {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <Field
          className={cn(smSpan[sm], mdSpan[md], lgSpan[lg], className)}
          data-invalid={fieldState.invalid}
        >
          <FieldLabel htmlFor={name}>{label}</FieldLabel>

          <AsyncSelect<TOption>
            label={label}
            disabled={disabled}
            useQuery={useQuery}
            getOptionValue={getOptionValue}
            getDisplayValue={getDisplayValue}
            placeholder={placeholder}
            value={
              (returnOption
                ? field.value
                  ? getOptionValue(field.value)
                  : ''
                : field.value) ?? ''
            }
            onChange={(value, option) => {
              field.onChange(returnOption ? option : value);
              onSelect?.(option);
            }}
            clearable={clearable}
          />

          {fieldState.error && <FieldError errors={[fieldState.error]} />}
        </Field>
      )}
    />
  );
}
